<template>
  <div
    ref="rootEl"
    class="product-finder-drawer fixed left-0 top-0 z-page-modal flex h-screen w-screen select-none items-center justify-center p-16 mobile-only:h-dvh"
    :class="{ 'is-loading': isLoading }"
  >
    <div
      class="drawer-background absolute inset-0 bg-black opacity-[.35]"
      @click="$emit('close')"
    />
    <FocusTrap>
      <div
        class="drawer-content absolute bottom-0 right-0 top-0 z-[999999999999] flex w-full flex-col bg-white lg:w-[700px]"
      >
        <div
          class="flex items-baseline border-b border-b-grey-light-01 p-16 md:px-64 md:py-24"
        >
          <h2
            class="typo-headline-4 uppercase"
            :class="{
              'font-bold': isRebranding,
            }"
          >
            Filter
          </h2>
          <div class="typo-title-2 ml-12 uppercase text-grey-dark-02">
            {{ totalLabel }}
          </div>
          <button class="-mb-12 -mr-12 ml-auto p-12" @click="$emit('close')">
            <SpriteSymbol class="h-30 w-30" name="close" />
          </button>
        </div>
        <div class="flex-1 overflow-y-scroll p-16 md:p-64">
          <ProductFinderFacetCollection
            v-if="collectionFacet"
            v-bind="collectionFacet"
            v-model="selected.collection"
          />
          <ProductFinderFacet
            v-for="(facet, index) in otherFacets"
            :key="facet.facetId"
            v-model="selected[facet.facetId]"
            :initial-open="index < 3"
            v-bind="facet"
          />
        </div>

        <div
          class="z-50 grid gap-8 border-t border-t-grey-light-01 p-16 md:px-64 md:py-24"
        >
          <button
            :disabled="!hasMadeChanges"
            class="button is-primary w-full"
            @click="apply"
          >
            {{ $texts('productFinder.applyButton', 'Apply') }}
          </button>
          <button class="button w-full" @click="reset">
            {{ $texts('productFinder.resetButton', 'Reset') }}
          </button>
        </div>
      </div>
    </FocusTrap>
  </div>
</template>

<script setup lang="ts">
import { FocusTrap } from 'focus-trap-vue'
import { onKeyDown } from '@vueuse/core'
import {
  type ProductFinderFacetFilter,
  ProductFinderMode,
} from '#graphql-operations'
import { falsy } from '~/helpers/type'

const props = defineProps<{
  mode: ProductFinderMode

  /**
   * The currently selected facets according to the URL state.
   */
  selectedFacets: Record<string, string[]>

  /**
   * The search text.
   */
  text: string | undefined
}>()

const route = useRoute()
const router = useRouter()
const { $texts } = useNuxtApp()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const rootEl = ref<HTMLElement | null>(null)

const isRebranding = await useFeatureFlag()

// The selected facets including the user's changes.
const selected = ref<Record<string, string[]>>({ ...props.selectedFacets })

// Toggle to indicate if any changes were made to the selected facets.
const hasMadeChanges = ref(false)

// The input for the GraphQL query to fetch the facets.
const facetsInput = computed<ProductFinderFacetFilter[]>(() =>
  Object.entries(selected.value)
    .map(([id, filter]) => {
      if (filter.length) {
        return { id, filter }
      }
      return null
    })
    .filter(falsy),
)

// Set the toggle to true whenever any selection changes.
watch(facetsInput, () => (hasMadeChanges.value = true))

// Loads the updated facets for the current facet selection.
const { data, status } = await useAsyncData(
  () =>
    useCachedGraphqlQuery('productFinderFacets', {
      mode: props.mode,
      facets: facetsInput.value,
      input: props.text || undefined,
    }).then((v) => v.data.findProducts),
  {
    watch: [facetsInput],
  },
)

const isLoading = computed(() => status.value === 'pending')

const facets = computed(() => data.value?.facets || [])
const totalLabel = computed(() => {
  const total = data.value?.total || 0
  if (total === 0) {
    return $texts('productFinder.totalZero', 'No products')
  } else if (total === 1) {
    return $texts('productFinder.totalSingular', '1 product')
  }
  return $texts('productFinder.totalPlural', '@count products').replace(
    '@count',
    total.toString(),
  )
})

// The special collection facet.
const collectionFacet = computed(() =>
  facets.value.find((v) => v.facetId === 'collection'),
)

// All other facets (checkbox, color).
const otherFacets = computed(() =>
  facets.value.filter((v) => v.facetId !== 'collection'),
)

// Apply the selected selection and close the sidebar.
const apply = () => {
  const query: Record<string, string> = Object.entries(selected.value).reduce<
    Record<string, string>
  >((newQuery, [facetId, filters]) => {
    if (filters.length) {
      newQuery['f:' + facetId] = filters.join('___')
    }
    return newQuery
  }, {})

  router.push({
    path: route.path,
    query: {
      text: route.query.text,
      ...query,
      mode: route.query.mode,
    },
  })

  emit('close')
}

// Reset the selected state to the selected state according to the URL.
const reset = () => {
  selected.value = {}
}

// Close sidebar when pressing the Escape key.
onKeyDown('Escape', () => emit('close'))

useBodyScrollLock(rootEl)
</script>

<style lang="postcss">
.product-finder-drawer.is-loading {
  @apply !cursor-wait;
  * {
    @apply !cursor-wait;
  }
}
</style>
